import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Car } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const categories = [
    { id: "all", name: "Alle Diensten" },
    { id: "interieur", name: "Interieur" },
    { id: "exterieur", name: "Exterieur" },
  ];

  const filteredServices =
    selectedCategory === "all"
      ? services
      : services.filter((s) => s.category === selectedCategory);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Onze Diensten
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Premium Auto Detailing
            </h1>
            <p className="text-gray-400 text-lg mt-6 leading-relaxed">
              Specialistische diensten die u niet bij de wasstraat of wasbox krijgt. Professionele resultaten, met oog voor detail.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-ug-black border-b border-white/5 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-3 text-sm font-bold tracking-wider uppercase transition-colors duration-300 ${
                  selectedCategory === cat.id
                    ? "bg-primary text-white"
                    : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-ug-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredServices.map((service, index) => (
                <div
                  key={service.id}
                  data-testid={`service-detail-${index}`}
                  className="group bg-ug-paper border border-white/5 hover:border-primary/30 transition-colors duration-500 overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Image */}
                    <div className="aspect-square md:aspect-auto overflow-hidden">
                      <img
                        src={service.image_url}
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col justify-between">
                      <div>
                        <span className="text-primary text-xs font-bold tracking-widest uppercase">
                          {service.category}
                        </span>
                        <h3 className="text-2xl font-heading font-bold uppercase tracking-wide text-white mt-2">
                          {service.name}
                        </h3>
                        <p className="text-gray-400 text-sm mt-4 leading-relaxed">
                          {service.description}
                        </p>

                        {/* Features */}
                        <ul className="mt-6 space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-sm text-gray-300">
                              <Check className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-8 pt-6 border-t border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-3xl font-heading font-black text-white">
                              va €{service.price.toFixed(0)}
                            </span>
                          </div>
                          <Link to="/boeken">
                            <Button
                              data-testid={`book-service-${index}`}
                              className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-6 py-3 text-xs transition-colors duration-300"
                            >
                              Boeken
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Voorrijkosten Info */}
      <section className="py-8 bg-ug-paper border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3 text-gray-400">
            <Car className="w-5 h-5 text-primary" />
            <p className="text-sm">
              <span className="text-white font-medium">Voorrijkosten:</span> Vanaf 10 km wordt €0,50 per km berekend
            </p>
          </div>
        </div>
      </section>

      {/* Custom Quote CTA */}
      <section className="py-20 bg-ug-paper">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-black uppercase tracking-tighter text-white">
            Andere Dienst Nodig?
          </h2>
          <p className="text-gray-400 mt-4 mb-8">
            Extra of andere diensten zijn beschikbaar op aanvraag. Neem contact op voor een offerte op maat.
          </p>
          <Link to="/offerte">
            <Button
              data-testid="custom-quote-btn"
              className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4 transition-colors duration-300 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)]"
            >
              Offerte Op Maat
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
